import {
  BookOpen,
  Bookmark,
  LayoutGrid,
  LogOut,
  Menu,
  PenLine,
  Sparkles,
  User,
  X,
  Bell,
  Shield,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { BrandStar } from './BrandStar'
import { NotificationBell } from './NotificationBell'
import { SearchBar } from './SearchBar'
import { SiteIcon } from './SiteIcon'
import { Button } from './ui/button'

const primaryLinks = [
  { to: '/', label: 'Home', icon: 'home' as const, matchExact: true },
  { to: '/browse', label: 'Content Records', icon: 'content-records' as const },
  { to: '/my-record', label: 'My Record', icon: 'my-record' as const },
  { to: '/personality-records', label: 'Personality Records', icon: 'personality-records' as const },
  { to: '/knowledge', label: 'Knowledge Base', icon: 'knowledge-base' as const },
] as const

const secondaryLinks = [
  { to: '/discover', label: 'Discover', icon: Sparkles },
  { to: '/charts', label: 'Charts', icon: LayoutGrid },
] as const

export function NavBar() {
  const { session, profile, loading: authLoading } = useAuth()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMod, setIsMod] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const showTopSearch = true

  useEffect(() => {
    if (profile?.role === 'moderator' || profile?.role === 'admin') setIsMod(true)
  }, [profile?.role])

  useEffect(() => {
    setProfileMenuOpen(false)
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleSignOut() {
    await supabase.auth.signOut()
    setProfileMenuOpen(false)
    navigate('/')
  }

  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-[rgba(67,16,7,0.08)] bg-white md:flex">
        <div className="relative flex justify-center overflow-hidden border-b border-crusta-500 bg-crusta-500 px-5 pb-5 pt-0">
          <BrandStar tone="white" className="absolute left-4 top-4 h-6 w-6 opacity-65" />
          <BrandStar tone="soft" className="absolute right-5 top-7 h-3.5 w-3.5 opacity-80" />
          <BrandStar tone="white" className="absolute right-12 bottom-5 h-5 w-5 opacity-35" />
          <Link to="/" className="inline-flex items-center z-10">
            <img src="/crrr.png?v=2" alt="ClosedRecord" className="logo-sign-swing h-40 w-auto object-contain -mt-2" />
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          <p className="px-3 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6266]">Navigate</p>
          <nav className="mt-3 space-y-1">
            {primaryLinks.map((link) => (
              <SidebarLink
                key={link.to}
                to={link.to}
                icon={link.icon}
                label={link.label}
                matchExact={'matchExact' in link ? Boolean(link.matchExact) : false}
              />
            ))}
            {session && (
              <SidebarLink
                to="/my-record"
                icon="my-record"
                label="My Record"
              />
            )}
          </nav>

          <p className="mt-8 px-3 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6266]">Summaries</p>
          <nav className="mt-3 space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-crusta-50 text-crusta-800 shadow-[inset_0_0_0_1px_rgba(251,114,40,0.12)]'
                    : 'text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800'
                }`
              }
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
                <BookOpen className="h-4 w-4" />
              </span>
              <span>Feed</span>
            </NavLink>
            <NavLink
              to="/summaries/browse"
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-crusta-50 text-crusta-800 shadow-[inset_0_0_0_1px_rgba(251,114,40,0.12)]'
                    : 'text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800'
                }`
              }
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
                <PenLine className="h-4 w-4" />
              </span>
              <span>Add Summary</span>
            </NavLink>
          </nav>

          <p className="mt-8 px-3 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6266]">Explore</p>
          <nav className="mt-3 space-y-1">
            {secondaryLinks.map((link) => (
              <SidebarLink key={link.to} to={link.to} icon={link.icon} label={link.label} />
            ))}
            {session && (
              <NavLink
                to="/notifications"
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-crusta-50 text-crusta-800 shadow-[inset_0_0_0_1px_rgba(251,114,40,0.12)]'
                      : 'text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800'
                  }`
                }
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
                  <Bell className="h-4 w-4" />
                </span>
                <span>Notifications</span>
              </NavLink>
            )}
            {isMod && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-crusta-50 text-crusta-800 shadow-[inset_0_0_0_1px_rgba(251,114,40,0.12)]'
                      : 'text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800'
                  }`
                }
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
                  <Shield className="h-4 w-4" />
                </span>
                <span>Admin</span>
              </NavLink>
            )}
          </nav>
        </div>

        <div className="border-t border-[rgba(67,16,7,0.08)] px-4 py-4">
          {session ? (
            <div className="space-y-2">
              <SidebarLink to={profile?.username ? `/profile/${profile.username}` : '/edit-profile'} icon="my-record" label={profile?.display_name ?? profile?.username ?? 'Profile'} />
              <SidebarLink to="/settings" icon="settings" label="Settings" />
              {profile?.is_creator ? <SidebarLink to="/dashboard" icon="dashboard" label="Creator Dashboard" /> : null}
            </div>
          ) : authLoading ? null : (
            <div className="space-y-2">
              <Button asChild className="w-full justify-center">
                <Link to="/signup">Create account</Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-center">
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          )}
        </div>
      </aside>

      <div className="sticky top-0 z-50 border-b border-[rgba(67,16,7,0.08)] bg-white/95 backdrop-blur md:hidden">
        <div className="relative flex items-center justify-center overflow-hidden bg-crusta-500 px-4 pb-3 pt-0 min-h-[4.5rem]">
          <BrandStar tone="white" className="absolute left-3 top-3 h-4 w-4 opacity-60" />
          <BrandStar tone="soft" className="absolute right-14 top-5 h-3 w-3 opacity-80" />
          <Link to="/" className="shrink-0 z-10">
            <img src="/crrr.png?v=2" alt="ClosedRecord" className="logo-sign-swing h-32 w-auto object-contain -mt-2" />
          </Link>
          <div className="absolute right-4 flex items-center gap-2 z-20">
            {session && <NotificationBell />}
            {!authLoading && !session ? (
              <Button asChild size="sm" className="hidden sm:inline-flex">
                <Link to="/signup">Create account</Link>
              </Button>
            ) : null}
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[rgba(67,16,7,0.1)] bg-crusta-50 text-crusta-800"
              onClick={() => setMobileMenuOpen((current) => !current)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {showTopSearch ? (
          <div className="border-t border-[rgba(67,16,7,0.08)] px-4 py-3">
            <SearchBar autoFocus={false} />
          </div>
        ) : null}
        {mobileMenuOpen ? (
          <div className="border-t border-[rgba(67,16,7,0.08)] px-4 py-4">
            <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8b6266]">Summaries</p>
            <MobileLink to="/" onClick={() => setMobileMenuOpen(false)} icon={BookOpen}>
              Feed
            </MobileLink>
            <MobileLink to="/summaries/browse" onClick={() => setMobileMenuOpen(false)} icon={PenLine}>
              Add Summary
            </MobileLink>
            <div className="my-3 border-t border-[rgba(67,16,7,0.08)]" />
            <div className="space-y-1">
              {primaryLinks.map((link) => (
                <MobileLink key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} icon={link.icon}>
                  {link.label}
                </MobileLink>
              ))}
              {secondaryLinks.map((link) => (
                <MobileLink key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} icon={link.icon}>
                  {link.label}
                </MobileLink>
              ))}
              {session && (
                <MobileLink to="/notifications" onClick={() => setMobileMenuOpen(false)} icon={Bell}>
                  Notifications
                </MobileLink>
              )}
              {isMod && (
                <MobileLink to="/admin" onClick={() => setMobileMenuOpen(false)} icon={Shield}>
                  Admin
                </MobileLink>
              )}
              <div className="my-3 border-t border-[rgba(67,16,7,0.08)]" />
              {session ? (
                <>
                  {profile?.username ? (
                  <MobileLink to={`/profile/${profile.username}`} onClick={() => setMobileMenuOpen(false)} icon="my-record">
                    View Profile
                  </MobileLink>
                  ) : null}
                  <MobileLink to="/edit-profile" onClick={() => setMobileMenuOpen(false)} icon="pen">
                    Edit Profile
                  </MobileLink>
                  <MobileLink to="/settings" onClick={() => setMobileMenuOpen(false)} icon="settings">
                    Settings
                  </MobileLink>
                  {profile?.is_creator ? (
                    <MobileLink to="/dashboard" onClick={() => setMobileMenuOpen(false)} icon="dashboard">
                      Creator Dashboard
                    </MobileLink>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-crusta-800 hover:bg-crusta-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <MobileLink to="/login" onClick={() => setMobileMenuOpen(false)} icon={User}>
                    Sign in
                  </MobileLink>
                  <MobileLink to="/signup" onClick={() => setMobileMenuOpen(false)} icon={Sparkles}>
                    Create account
                  </MobileLink>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {showTopSearch ? (
        <div className="fixed left-60 right-0 top-0 z-30 hidden border-b border-[rgba(67,16,7,0.08)] bg-white/94 backdrop-blur md:block">
          <div className="flex items-center gap-5 px-8 py-4">
            <div className="min-w-0 flex-1">
              <SearchBar autoFocus={false} />
            </div>
            {session && <NotificationBell />}
            <Button asChild className="shrink-0">
              <Link to="/summaries/browse">
                <PenLine className="mr-2 h-4 w-4" />
                Add Summary
              </Link>
            </Button>
            {!authLoading && !session ? (
              <div className="flex items-center gap-2">
                <Button asChild variant="outline">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild>
                  <Link to="/signup">Create a free account</Link>
                </Button>
              </div>
            ) : session ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setProfileMenuOpen((current) => !current)}
                  className="flex h-11 items-center gap-3 rounded-xl border border-[rgba(67,16,7,0.1)] bg-crusta-50 px-3 text-sm font-semibold text-crusta-800"
                >
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-crusta-800 text-crusta-50">
                    {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" /> : <User className="h-4 w-4" />}
                  </div>
                  <span className="max-w-[140px] truncate">{profile?.display_name ?? profile?.username ?? 'Account'}</span>
                </button>
                <div className={`absolute right-0 top-[calc(100%+0.6rem)] w-56 overflow-hidden rounded-2xl border border-[rgba(67,16,7,0.1)] bg-white p-2 shadow-[0_18px_38px_rgba(155,43,17,0.12)] transition-all ${profileMenuOpen ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-2 opacity-0'}`}>
                  {profile?.username ? (
                    <DesktopMenuLink to={`/profile/${profile.username}`} onClick={() => setProfileMenuOpen(false)}>
                      <SiteIcon name="my-record" className="h-4 w-4" />
                      View profile
                    </DesktopMenuLink>
                  ) : null}
                  <DesktopMenuLink to="/reading-list" onClick={() => setProfileMenuOpen(false)}>
                    <Bookmark className="h-4 w-4" />
                    Reading list
                  </DesktopMenuLink>
                  <DesktopMenuLink to="/edit-profile" onClick={() => setProfileMenuOpen(false)}>
                    <SiteIcon name="pen" className="h-4 w-4" />
                    Edit profile
                  </DesktopMenuLink>
                  <DesktopMenuLink to="/settings" onClick={() => setProfileMenuOpen(false)}>
                    <SiteIcon name="settings" className="h-4 w-4" />
                    Settings
                  </DesktopMenuLink>
                  {profile?.is_creator ? (
                    <DesktopMenuLink to="/dashboard" onClick={() => setProfileMenuOpen(false)}>
                      <SiteIcon name="dashboard" className="h-4 w-4" />
                      Creator dashboard
                    </DesktopMenuLink>
                  ) : null}
                  <div className="my-2 border-t border-[rgba(67,16,7,0.08)]" />
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-crusta-800 hover:bg-crusta-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  )
}

function SidebarLink({
  to,
  icon: Icon,
  label,
  matchExact = false,
}: {
  to: string
  icon:
    | 'home'
    | 'content-records'
    | 'personality-records'
    | 'knowledge-base'
    | 'my-record'
    | 'settings'
    | 'dashboard'
    | typeof Sparkles
    | typeof LayoutGrid
  label: string
  matchExact?: boolean
}) {
  return (
    <NavLink
      to={to}
      end={matchExact}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
          isActive
            ? 'bg-crusta-50 text-crusta-800 shadow-[inset_0_0_0_1px_rgba(251,114,40,0.12)]'
            : 'text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800'
        }`
      }
    >
      <span className="flex h-9 w-9 items-center justify-center">
        {typeof Icon === 'string' ? (
          <SiteIcon name={Icon} className="h-9 w-9" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
            <Icon className="h-4 w-4" />
          </span>
        )}
      </span>
      <span>{label}</span>
    </NavLink>
  )
}

function MobileLink({
  to,
  onClick,
  children,
  icon: Icon,
}: {
  to: string
  onClick?: () => void
  children: React.ReactNode
  icon:
    | 'home'
    | 'content-records'
    | 'personality-records'
    | 'knowledge-base'
    | 'my-record'
    | 'settings'
    | 'dashboard'
    | 'pen'
    | typeof Sparkles
    | typeof User
    | typeof BookOpen
    | typeof PenLine
    | typeof Bell
    | typeof Shield
}) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800">
      <span className="flex h-9 w-9 items-center justify-center">
        {typeof Icon === 'string' ? (
          <SiteIcon name={Icon} className="h-9 w-9" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crusta-50 text-crusta-800">
            <Icon className="h-4 w-4" />
          </span>
        )}
      </span>
      {children}
    </Link>
  )
}

function DesktopMenuLink({ to, onClick, children }: { to: string; onClick?: () => void; children: React.ReactNode }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-crusta-800 hover:bg-crusta-50 hover:text-crusta-800">
      {children}
    </Link>
  )
}
